<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import subDays from 'date-fns/subDays';
import VenuesAPI from 'dashboard/api/venues';
import MetricCard from './overview/MetricCard.vue';
import BuzzVenueOverviewCard from './BuzzVenueOverviewCard.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const buzzLoading = ref(false);
const venueBuzzData = reactive({});

const venues = computed(() =>
  [...(store.getters['venues/getVenues'] || [])].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  )
);
const venuesLoading = computed(() => store.state.venues.uiFlags.isFetching);
const isLoading = computed(() => venuesLoading.value);

const historyRange = () => {
  const to = new Date();
  const from = subDays(to, 90);
  return { from: from.toISOString(), to: to.toISOString() };
};

const loadVenueHistory = async venue => {
  try {
    const response = await VenuesAPI.getBuzz(venue.id, historyRange());
    venueBuzzData[venue.id] = {
      history: response.data?.history || [],
      bp: response.data?.bp ?? response.data?.summary?.bp ?? null,
    };
  } catch {
    venueBuzzData[venue.id] = { history: [], bp: null };
  }
};

const loadAllHistory = async venueList => {
  buzzLoading.value = true;
  await Promise.all(venueList.map(loadVenueHistory));
  buzzLoading.value = false;
};

const openVenueBuzz = venue => {
  router.push({
    name: 'buzz_reports',
    params: { accountId: route.params.accountId },
    query: { venue_id: venue.id },
  });
};

onMounted(async () => {
  await store.dispatch('venues/get');
  if (venues.value.length) {
    await loadAllHistory(venues.value);
  }
});
</script>

<template>
  <MetricCard
    :header="t('OVERVIEW_REPORTS.BUZZ_SCORES.HEADER')"
    :is-loading="isLoading"
    :loading-message="t('OVERVIEW_REPORTS.BUZZ_SCORES.LOADING_MESSAGE')"
  >
    <div class="w-full">
      <div v-if="!venues.length" class="text-n-slate-11 text-sm mb-0">
        {{ t('OVERVIEW_REPORTS.BUZZ_SCORES.EMPTY') }}
      </div>
      <div v-else class="grid w-full gap-4 md:grid-cols-2">
        <BuzzVenueOverviewCard
          v-for="venue in venues"
          :key="venue.id"
          :venue="venue"
          :history="venueBuzzData[venue.id]?.history || []"
          :period-delta="venueBuzzData[venue.id]?.bp"
          @select="openVenueBuzz"
        />
      </div>
    </div>
  </MetricCard>
</template>
